import {
    TRANSPORT_LOCATIONS,
    findTransportLocation,
    getAvailableTransportLocations,
} from "@elit-hair/elitklinik-shared";
import { Box, Chip, Tooltip, tooltipClasses } from "@mui/material";
import { GridApiCommon, GridColDef, GridRenderEditCellParams, getGridSingleSelectOperators } from "@mui/x-data-grid";
import { createElement, memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { TbBuildingAirport, TbInfoCircle } from "react-icons/tb";

import { TableHeader } from "@/components/TableHeader";
import MemoizedEditAutoComplete from "@/components/table/EditAutoCompletete";
import MemoizedEditableColumn from "@/components/table/EditableColumn";
import { Airpots, airpots } from "@/constants/airpots";
import { operationStatuses } from "@/constants/operationStatuses";
import { getTransportLocationTypeLabel } from "@/constants/transferLocations";
import { Size } from "@/hooks/useWindowDimensions";
import { NoMaxWidthTooltip } from "@/pages/DriverStatus.tsx/components/DriverStatusColumnTooltip";
import HotelStatusTooltip from "@/pages/HotelPlanning/arrival/components/table/components/HotelStatusTooltip";
import { usePlanActions, usePlanChangedVal } from "@/stores/use-plan-table";
import { IPlanPassenger } from "@/types/tasks.types";

import { MemoizedLocationChip } from "./PlanLocationColumn";

export const PlanAirportColumn = ({
    headerName,
    size,
    editable,
    valueGetter,
}: {
    headerName: string;
    size: Size;
    editable: boolean;
    valueGetter: (row: IPlanPassenger<"api">) => string;
}): GridColDef<IPlanPassenger<"api">> => {
    return {
        field: "airport",
        headerName,
        minWidth: size.md ? 190 : 90,
        type: "singleSelect",
        valueOptions: TRANSPORT_LOCATIONS,
        editable,
        valueGetter: (_val, row) => valueGetter(row),
        pastedValueParser: (value: string) => {
            const location = findTransportLocation(value);

            return location?.value || undefined;
        },
        filterOperators: getGridSingleSelectOperators(),
        renderHeader: (params) => <TableHeader Component={TbBuildingAirport} title={params.colDef.headerName!} />,
        renderCell: (params) => (
            <MemoComponent
                editable={editable}
                api={params.api}
                row={params.row}
                field={params.field}
                value={params.value}
            />
        ),
        renderEditCell: (params) => <EditCell params={params} />,
    };
};

export const MemoizedCell = memo(({ value, isFlightArrival }: { value: string | number; isFlightArrival: boolean }) => {
    const { t } = useTranslation();
    const airport = useMemo(() => value && findTransportLocation(value), [value]);

    const status = useMemo(
        () => operationStatuses.find((item) => item.cond(false, isFlightArrival, true)),
        [value, isFlightArrival]
    );

    return (
        <div className="flex items-center h-full py-1">
            {status && airport && (
                <Tooltip
                    title={
                        <div className="flex items-center gap-1">
                            {createElement(status.component(), { fontSize: 16 })}
                            {airport
                                ? t(airport.label) + " " + t("operationStatuses." + isFlightArrival)
                                : t("operationStatuses." + isFlightArrival)}
                        </div>
                    }
                >
                    <Chip
                        sx={{
                            backgroundColor: status.bg,
                            color: status.text,
                            fontWeight: 500,
                            fontSize: "12px !important",
                        }}
                        icon={createElement(status.component(), { fontSize: 14, color: status.text })}
                        label={(airport.label ? `${airport.label}-` : "") + t("operationStatuses." + isFlightArrival)}
                    />
                </Tooltip>
            )}
        </div>
    );
});

const MemoComponent = memo(
    ({
        value,
        row,
        field,
        api,
        editable,
    }: {
        api: GridApiCommon;
        field: string;
        row: IPlanPassenger<"api"> & { isArrival?: boolean };
        value: string;
        editable: boolean;
    }) => {
        return (
            <MemoizedEditableColumn
                renderHistoryComp={(val) => (
                    <MemoizedCell value={val as string} isFlightArrival={!!(row.isFlightArrival || row.isArrival)} />
                )}
                api={api}
                field={field}
                id={row.id}
                editable={editable}
            >
                <MemoizedCell isFlightArrival={row.isFlightArrival} value={value} />
            </MemoizedEditableColumn>
        );
    }
);

function EditCell({ params }: { params: GridRenderEditCellParams<IPlanPassenger<"api">> }) {
    const { processRowUpdate } = usePlanActions();
    const changedLocation = usePlanChangedVal(params.id, "location");
    const location = changedLocation || params.row.location;
    const options = useMemo(
        () => getAvailableTransportLocations(params.row.isFlightArrival, params.row.isFlightArrival),
        [params.row.isFlightArrival]
    );

    return (
        <MemoizedEditAutoComplete
            processRowUpdate={processRowUpdate}
            renderHistoryComp={(val) => <MemoizedCell value={val} isFlightArrival={params.row?.isFlightArrival} />}
            groupBy={getTransportLocationTypeLabel}
            row={params.row}
            field={params.field}
            api={params.api}
            value={params.value}
            options={options}
            optionComp={MemoizedLocationChip}
            icon={TbBuildingAirport}
            excludeValue={location as string | number}
        />
    );
}

export function AirportOption({ value }: { value: string | number }) {
    const option = airpots.find((item) => {
        return item.values.includes(+value || (value as Airpots | number));
    });

    const { t } = useTranslation();
    if (!option) return <span></span>;
    return (
        <Box
            key={value}
            component="span"
            className="flex items-center text-base font-medium justify-between  w-full  gap-1"
        >
            <div className="flex items-center gap-1  font-medium whitespace-nowrap">
                <img src={option.img} className="w-5 h-5  rounded shadow" alt="" />

                {t(option.value)}
            </div>
            <NoMaxWidthTooltip
                arrow
                sx={{
                    [`& .${tooltipClasses.tooltip}`]: {
                        backgroundColor: "white",
                        border: "none",
                        padding: "0px !important",
                        fontSize: 12,
                        maxWidth: "200px !important",
                    },
                }}
                title={<HotelStatusTooltip status={option} />}
            >
                <div className="ml-auto ">
                    <TbInfoCircle />
                </div>
            </NoMaxWidthTooltip>
        </Box>
    );
}
